// Shared types and utilities

export interface Shop {
  id: string;
  name: string;
  logoUrl?: string;
  email?: string;
  phone?: string;
  notes?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  defaultLaborRate?: string;
  taxRate?: string;
  createdAt: Date;
}

export interface Customer {
  id: string;
  shopId: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  notes?: string;
  createdAt: Date;
}

export interface Vehicle {
  id: string;
  shopId: string;
  customerId?: string | null;
  vin?: string;
  year?: number;
  make?: string;
  model?: string;
  trim?: string;
  engineDisplacementL?: string | number;
  engineCylinders?: number;
  engineDescription?: string;
  drivetrain?: string;
  engineType?: string;
  engineCode?: string;
  mileage?: number;
  currentMileage?: number;
  oilCapacityQuarts?: string;
  oilType?: string;
  licensePlate?: string;
  licensePlateState?: string;
  notes?: string;
  createdAt: Date;
}

// Legacy job status (deprecated)
export type JobStatus = "open" | "in_progress" | "waiting_parts" | "completed" | "invoiced" | "paid";

// New job stages
export type JobType = "general" | "oil_change" | "preventative" | "diagnostic" | "other";
export type JobStage = "quote" | "work_order" | "hold" | "invoice";
export type ApprovalStatus = "not_required" | "pending" | "approved" | "needs_reapproval";
export type ApprovalMethod = "signature" | "magic_link";
export type PaymentProvider = "stripe" | "cash" | "venmo" | "check" | "other";
export type PaymentStatus = "pending" | "processing" | "completed" | "failed" | "refunded";

export interface JobLineItem {
  id: string;
  jobId: string;
  type: "labor" | "part" | "subcontract" | "misc";
  description: string;
  notes?: string;
  quantity: string;
  unitPrice: string;
  markupPercent: string;
  total: string;
  catalogItemId?: string;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface JobApproval {
  id: string;
  jobId: string;
  approvedAmount: string;
  approvedByName?: string;
  approvedByEmail?: string;
  approvalMethod: ApprovalMethod;
  approvalToken?: string;
  signatureData?: string;
  ipAddress?: string;
  userAgent?: string;
  approvedAt: Date;
  createdAt: Date;
}

export interface JobPayment {
  id: string;
  jobId: string;
  amount: string;
  provider: PaymentProvider;
  status: PaymentStatus;
  stripePaymentIntentId?: string;
  stripeCheckoutSessionId?: string;
  stripeChargeId?: string;
  notes?: string;
  processedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface Job {
  id: string;
  shopId: string;
  vehicleId: string;
  customerId: string;
  jobType: JobType;
  stage: JobStage;
  approvalStatus: ApprovalStatus;
  mileage?: number;
  subtotal: string;
  taxAmount: string;
  fees: string;
  discount: string;
  total: string;
  depositRequired: boolean;
  depositAmount?: string;
  depositPercent?: string;
  // Legacy field (deprecated)
  status?: JobStatus;
  title?: string;
  description?: string;
  scheduledDate?: Date;
  workStartedAt?: Date;
  workCompletedAt?: Date;
  completedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  // Related data
  lineItems?: JobLineItem[];
  approvals?: JobApproval[];
  payments?: JobPayment[];
  customer?: Customer;
  vehicle?: Vehicle & { customer?: Customer | null };
  shop?: Shop;
}

export interface JobComputedFields {
  approvalStatus: ApprovalStatus;
  needsReapproval: boolean;
  latestApprovedAmount: string | null;
  totalPaid: string;
  balanceDue: string;
  isPaidInFull: boolean;
  currentMileage?: number | null;
  previousMileage?: number | null;
  lastOilChangeMileage?: number | null;
  milesSinceLastOilChange?: number | null;
}

export interface JobWithComputed extends Job {
  computed: JobComputedFields;
}

export type QuoteStatus = "draft" | "sent" | "viewed" | "approved" | "declined" | "expired" | "superseded";

export interface LineItem {
  id: string;
  type: "labor" | "part" | "subcontract" | "misc";
  description: string;
  quantity: string;
  unitPrice: string;
  markupPercent: string;
  total: string;
}

export interface Quote {
  id: string;
  shopId: string;
  jobId: string;
  status: QuoteStatus;
  quoteNumber: string;
  versionNumber: number;
  subtotal: string;
  taxAmount: string;
  fees: string;
  discount: string;
  total: string;
  depositRequired: boolean;
  depositPercent?: string;
  depositAmount?: string;
  expiresAt?: Date;
  notes?: string;
  approvedAt?: Date;
  approvedByName?: string;
  createdAt: Date;
}

export type InvoiceStatus = "draft" | "sent" | "paid" | "overdue" | "void";

export interface Invoice {
  id: string;
  shopId: string;
  jobId: string;
  quoteId?: string;
  invoiceNumber: string;
  status: InvoiceStatus;
  subtotal: string;
  taxAmount: string;
  total: string;
  depositApplied: string;
  balanceDue: string;
  dueDate?: Date;
  notes?: string;
  paidAt?: Date;
  createdAt: Date;
}

// Utility functions
export function formatCurrency(amount: string | number): string {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(num);
}

export function formatDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function calculateTotals(lineItems: Array<{ quantity: number; unitPrice: number; markupPercent?: number }>) {
  const subtotalCents = lineItems.reduce((sum, item) => {
    const quantityHundredths = Math.round(item.quantity * 100);
    const unitPriceCents = Math.round(item.unitPrice * 100);
    const markupBasisPoints = Math.round((item.markupPercent || 0) * 100);
    const itemCents = Math.round(
      Math.abs(quantityHundredths * unitPriceCents * (10000 + markupBasisPoints)) / 1_000_000
    );
    return sum + itemCents;
  }, 0);
  
  return {
    subtotal: subtotalCents / 100,
    total: subtotalCents / 100,
  };
}

// Vehicle Year/Make/Model data and utilities
export {
  vehicleData,
  getYears,
  getMakes,
  getModels,
  getEngines,
  parseEngine,
  generateEngineDescription,
} from './vehicleData';

export type {
  VehicleData,
  ParsedEngine,
} from './vehicleData';
